import { Link } from 'react-router-dom';
import { money } from '../../../lib/api';
import type { AllowanceBalance } from '../../../shared/types';
import type { Resource } from '../useResource';
import { Card, CardTitle, Empty, ErrorState, Icon, LoadingRows } from '../ui';

/**
 * A kid's own allowance balance. This is the only money on the kid view — their
 * money, earned by them. No household finances, ever.
 */
export default function AllowanceCard({
  res,
  color,
}: {
  res: Resource<AllowanceBalance>;
  color: string | null;
}) {
  const { data, loading, error, reload } = res;

  return (
    <Card className="p-5 sm:p-6">
      <CardTitle icon="coin" action={{ to: '/chores', label: 'Chores' }}>
        Your allowance
      </CardTitle>

      <div className="mt-4">
        {loading && <LoadingRows rows={2} />}
        {!loading && error && <ErrorState what="your allowance" onRetry={reload} />}

        {/* `!data` covers the case where there's no ledger for this member yet. */}
        {!loading && !error && (!data || (data.balance === 0 && data.pending === 0)) && (
          <Empty
            message="Nothing in your allowance yet. Finish a chore and your first dollar lands right here."
            action={{ to: '/chores', label: 'See your chores' }}
          />
        )}

        {!loading && !error && data && (data.balance !== 0 || data.pending !== 0) && (
          <>
            <p
              className="font-display text-5xl leading-none font-semibold tabular-nums"
              style={{ color: color ?? 'var(--color-ink)' }}
            >
              {money(data.balance)}
            </p>
            <p className="mt-2 text-sm text-ink-soft">
              {data.pending > 0 ? 'is yours right now' : 'is yours — all caught up'}
            </p>

            {data.pending > 0 && (
              <p className="mt-3 flex items-start gap-2 rounded-xl bg-honey-soft px-3 py-2.5 text-sm text-ink">
                <Icon name="alert" className="mt-0.5 h-4 w-4 shrink-0 text-honey" />
                <span>
                  <span className="font-semibold">{money(data.pending)}</span> more is waiting for a
                  grown-up to approve it.
                </span>
              </p>
            )}

            <Link
              to="/chores"
              className="mt-3 inline-flex min-h-11 items-center gap-1.5 text-sm font-medium text-sage hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
            >
              See how you earned it
              <Icon name="arrow" className="h-4 w-4" />
            </Link>
          </>
        )}
      </div>
    </Card>
  );
}
